import _ from 'lodash';

import { composeKey, filterQueriesByTemplate } from '../../utils/dashboards';
import { chartAxes } from '../../constants/event';
import { sortingOptions, sortingOrder } from '../../constants/common';

const initialQuery = {
  result: {},
  error: false,
  isExecute: false,
};

const initialExecutionTemplate = {
  isChecked: false,
  executionTime: 0,
  range: {
    from: 0,
    to: 0,
  },
};

const initialState = {
  queries: {},
  templatesState: {},
  result: {},
};

const columnChartSortingInitialState = {
  axis: chartAxes.Y,
  order: sortingOrder.DESCENDING,
  sortingOptions: sortingOptions.INDEPEND,
};

export const executeEventOptions = {
  columnChartSorting: columnChartSortingInitialState,
  views: [],
  view: undefined,
  singleChartMode: true,
  showFormula: false,
  calcTotal: false,
  averageLine: false,
  medianLine: false,
  trendLine: false,
  showSortingOptions: false,
  enteredFormula: null,
  adaptive: false,
};

const execute = (state = initialState, action) => {
  switch (action.type) {
    case 'EXECUTE_DASHBOARD_UNIT_QUERIES_START': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            ...state.result[action.unit.id],
            isExecute: true,
            hasError: false,
            executeId: action.executeId,
            requestCanceled: false,
          },
        },
      };
    }

    case 'CANCEL_DASHBOARD_UNIT_REQUEST': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unitId]: {
            ...state.result[action.unitId],
            executeId: '',
            requestCanceled: true,
          },
        },
      };
    }

    case 'EXECUTE_DASHBOARD_UNIT_QUERIES_ERROR':
      return {
        ...state,
        result: {
          ...state.result,
          [action.id]: {
            ...state.result[action.id],
            isExecute: false,
            hasError: true,
            executeId: '',
          },
        },
      };

    case 'EXECUTE_DASHBOARD_EVENT_QUERIES_SUCCESS': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            executeTime: new Date().valueOf(),
            events: action.result,
            isExecute: false,
            hasError: false,
          },
        },
      };
    }

    case 'EXECUTE_DASHBOARD_REVENUE_QUERIES_SUCCESS': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            executeTime: new Date().valueOf(),
            events: action.events,
            isExecute: false,
            hasError: false,
          },
        },
      };
    }

    case 'EXECUTE_DASHBOARD_USERS_QUERY_SUCCESS': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            executeTime: new Date().valueOf(),
            isExecute: false,
            hasError: false,
            events: action.events,
          },
        },
      };
    }

    case 'EXECUTE_DASHBOARD_RETENTION_SIMPLE_QUERY_SUCCESS': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            isExecute: false,
            hasError: false,
            result: action.result,
            query: action.query,
            executeTime: new Date().valueOf(),
          },
        },
      };
    }

    case 'EXECUTE_DASHBOARD_RETENTION_COMPLEX_QUERY_SUCCESS': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            isExecute: false,
            hasError: false,
            result: action.result,
            query: action.query,
            executeTime: new Date().valueOf(),
          },
        },
      };
    }

    case 'DASHBOARD_EXECUTE_FUNNEL_SUCCESS': {
      return {
        ...state,
        result: {
          ...state.result,
          [action.unit.id]: {
            isExecute: false,
            hasError: false,
            result: action.result,
            executeTime: new Date().valueOf(),
          },
        },
      };
    }

    case 'TOGGLE_TEMPLATE': {
      const key = composeKey(action.tenantId, action.dashboardId, action.templateId);
      const prevState = _.defaults(state.templatesState[key], initialExecutionTemplate);
      return {
        ...state,
        templatesState: {
          ...state.templatesState,
          [key]: {
            ...prevState,
            templateId: action.templateId,
            isChecked: action.value,
          },
        },
      };
    }

    case 'EXECUTE_TEMPLATE':
      return {
        ...state,
        templatesState: {
          ...state.templatesState,
          [composeKey(action.tenantId, action.dashboardId, action.templateId)]: {
            templateId: action.templateId,
            executionTime: Date.now(),
            range: action.range,
          },
        },
      };

    case 'START_EXECUTE_TEMPLATE_QUERY':
      return {
        ...state,
        queries: {
          ...state.queries,
          [composeKey(action.tenantId, action.dashboardId, action.templateId, action.queryId)]: {
            ...initialQuery,
            id: action.queryId,
            templateId: action.templateId,
            dashboardId: action.dashboardId,
            isExecute: true,
          },
        },
      };

    case 'EXECUTE_TEMPLATE_QUERY_SUCCESS':
    case 'EXECUTE_TEMPLATE_QUERY_ERROR':
      return {
        ...state,
        queries: {
          ...state.queries,
          [composeKey(action.tenantId, action.dashboardId, action.templateId, action.queryId)]: {
            ...initialQuery,
            ...(state.queries[
              composeKey(action.tenantId, action.dashboardId, action.templateId, action.queryId)
            ] || {}),
            isExecute: false,
            templateId: action.templateId,
            dashboardId: action.dashboardId,
            error: action.error || false,
            result: action.result || {},
          },
        },
      };

    case 'UPDATE_TEMPLATE_SUCCESS': {
      const filteredQueries = Object.entries(state.queries).filter(
        ([key]) => !filterQueriesByTemplate(key, action.template.id)
      );
      const filteredTemplates = Object.entries(state.templatesState).filter(
        ([key]) => !key.endsWith(action.template.id)
      );

      return {
        ...state,
        queries: Object.fromEntries(filteredQueries),
        templatesState: Object.fromEntries(filteredTemplates),
      };
    }

    case 'SELECT_DASHBOARD':
    case 'RESET_DASHBOARD':
    case 'COPY_DASHBOARD':
    case 'COMPOSE_DASHBOARD':
    case 'DELETE_DASHBOARD_SUCCESS':
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

export default execute;

export const getQueries = (state) => state.queries;

export const getTemplatesState = (state) => state.templatesState;

export const getUnitsExecutionState = (state) => state.result;
